import React, { useCallback, useState } from 'react';
import { Add as PlusIcon } from '@mui/icons-material';
import { T, fontBody } from '../tokens';
import { parseDragPayload } from '../utils/flowUtils';
import FlowNode from './FlowNode';
import FlowConnector from './FlowConnector';
import FlowDropZone from './FlowDropZone';

export default function FlowCanvas({
  flow,
  selectedNode,
  onSelectNode,
  onReorderNode,
  onInsertFromLibrary,
}) {
  const [dropIndex, setDropIndex] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const isDragging = dragActive || draggingIndex !== null || dropIndex !== null;

  const finishDrag = useCallback(() => {
    setDraggingIndex(null);
    setDropIndex(null);
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((targetIndex, event) => {
    event.preventDefault();
    event.stopPropagation();

    const payload = parseDragPayload(event);
    finishDrag();
    if (!payload) return;

    if (payload.source === 'canvas') {
      onReorderNode(payload.index, targetIndex);
      return;
    }

    if (payload.source === 'library') {
      onInsertFromLibrary(payload.itemId, targetIndex);
    }
  }, [finishDrag, onInsertFromLibrary, onReorderNode]);

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div
      className="sma-scroll"
      onDragEnter={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragOver={handleCanvasDragOver}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) finishDrag();
      }}
      style={{
        overflowY: 'auto', padding: '28px 0', display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}
    >
      {flow.map((node, i) => (
        <React.Fragment key={node.uid || `${node.id}-${i}`}>
          <FlowDropZone
            index={i}
            active={dropIndex === i}
            expanded={isDragging}
            onDragOver={setDropIndex}
            onDrop={handleDrop}
          />
          <FlowNode
            node={node}
            index={i}
            onClick={onSelectNode}
            selected={Boolean(selectedNode && (
              (selectedNode.uid && node.uid && selectedNode.uid === node.uid)
              || (!selectedNode.uid && selectedNode.id === node.id && selectedNode.kind === node.kind)
            ))}
            dragging={draggingIndex === i}
            dropPosition={dropIndex}
            onDragStart={(_e, index) => setDraggingIndex(index)}
            onDragEnd={finishDrag}
            onNodeDragOver={setDropIndex}
            onNodeDrop={handleDrop}
          />
          {i < flow.length - 1 && (
            <FlowConnector
              index={i + 1}
              active={dropIndex === i + 1}
              expanded={isDragging}
              onDragOver={setDropIndex}
              onDrop={handleDrop}
            />
          )}
        </React.Fragment>
      ))}

      <FlowDropZone
        index={flow.length}
        active={dropIndex === flow.length}
        expanded={isDragging}
        onDragOver={setDropIndex}
        onDrop={handleDrop}
      />

      <div
        style={{ marginTop: 8 }}
        onDragOver={(e) => {
          e.preventDefault();
          setDropIndex(flow.length);
        }}
        onDrop={(e) => handleDrop(flow.length, e)}
      >
        <FlowConnector />
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto',
            fontFamily: fontBody, fontSize: 12.5, fontWeight: 600, color: T.slate,
            background: dropIndex === flow.length ? T.blueSoft : 'transparent',
            border: `1.5px dashed ${dropIndex === flow.length ? T.blue : T.line}`,
            padding: '8px 14px', borderRadius: 10, cursor: 'default',
          }}
        >
          <PlusIcon sx={{ fontSize: 13 }} /> Drag steps here
        </div>
      </div>
    </div>
  );
}
